import  { useState, FC, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useApi } from "../../api/ApiProvider";
import Api from "../../axiosApi/api";
import { useToastMessageQueue } from "../ToastMessages/ToastMessageProvider";
import intl from "components/i18n/ReactIntlWrapper";
import { useWorkflow } from "./WorkflowContext";
import WorkflowCommentsModal from "./WorkflowCommentsModal";
import { Box, Button, LinearProgress, Tooltip } from "@mui/material";
import MuiIconDisplay from "components/MuiIconPicker/MuiIconDisplay";
import { UpdateStateCommand } from "axiosApi/models/update-state-command";
import { handleAPIError } from "common/errorHandler";
import Spinner from "../Spinner";

export type IEditingModeSetings = {
  edit: boolean;
  view: boolean;
  delete: boolean;
  editByOwner: boolean;
  viewByOwner: boolean;
  deleteByOwner: boolean;
};

export type handleEditingMode = (IEditingModeSetings) => void;
export type WorkflowActionClickParams = (
  stateId: number,
  comments?: string
) => void;

type WorkflowActionsBarProps = {
  viewMode: string;
  onActionClick: WorkflowActionClickParams;
  loading: boolean;
  id: number;
  stateId: number;
  handleEditingMode?: handleEditingMode;
  errors?: Object;
  idTimeOffRequestType?: number;
  disabled?: boolean;
};

const WorkflowActionsBar: FC<WorkflowActionsBarProps> = ({
  viewMode,
  onActionClick,
  loading: parentLoading,
  id,
  stateId,
  handleEditingMode,
  disabled,
}) => {
  const { workflow, loading, errors: error } = useWorkflow();
  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const navigate = useNavigate();
  const [entityId, setEntityId] = useState<number | null>(id);

  const [errors, setErrors] = useState<number | null>(null);

  const [isWorkflowCommentsOpen, setIsWorkflowCommentsOpen] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<number | null>(null);
  const [loadingActionId, setLoadingActionId] = useState<number | null>(null);

  const closeWorkflowComments = () => {
    setIsWorkflowCommentsOpen(false);
    setLoadingActionId(null);
  };
  const handleClick = async (id: number, showComments: boolean) => {
    setLoadingActionId(id);
    setIsWorkflowCommentsOpen(showComments);
    if (!showComments) {
      try {
        await UpdateState(id);
      } finally {
        setLoadingActionId(null);
      }
    } else {
      setPendingActionId(id);
    }
  };

  const onWorkflowCommentsModalSubmitted = async (comments: string) => {
    if (!pendingActionId) {
      toast.error({ message: intl.get("error") });
      return;
    }

    setLoadingActionId(pendingActionId);
    try {
      await UpdateState(pendingActionId, comments);
    } finally {
      setLoadingActionId(null);
    }
  };

  useEffect(() => {
    setEntityId(id);
  }, [id]);

  const UpdateState = async (id: number, comments: string = null) => {
    if (entityId == null) {
      onActionClick(id, comments);
      return;
    }
    var command: UpdateStateCommand = {
      comments: comments,
      workflowStateId: id,
      entityId: entityId,
      moduleId: workflow.idModule,
    };

    try {
      const response = await api.workflowApi.apiVversionWorkflowUpdateStatePost("1", command);
      if (response.status === 200) {
        const result = await onActionClick(id, comments);
        return result;
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...error });
      return null;
    } finally {
      setLoadingActionId(null);
    }
  };

  const handleBackClick = (e) => {
    navigate(-1);
  };

  //when the workflow has loaded, call the parent component to enable or disable the edit, view and delete buttons
  useEffect(() => {
    if (handleEditingMode) {
      if (workflow) {
        if (stateId == null) {
          if (id == null)
            handleEditingMode({
              edit: true,
              view: true,
              delete: true,
              editByOwner: true,
              viewByOwner: true,
              deleteByOwner: true,
            });
          else
            handleEditingMode({
              edit: false,
              view: false,
              delete: false,
              editByOwner: false,
              viewByOwner: false,
              deleteByOwner: false,
            });

          return;
        }
        var state = workflow?.states.find((x) => x.id == stateId);
        handleEditingMode({
          edit: state?.editable,
          view: state?.viewable,
          delete: state?.deletable,
          editByOwner: state?.editableByOwner,
          viewByOwner: state?.viewableByOwner,
          deleteByOwner: state?.deletableByOwner,
        });
      }
    }
  }, [workflow, stateId, id]);

  return (
    <Box
      className="workflow-actions-bar"
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexWrap="wrap"
      gap={1}
      height="100%"
      width="100%"
    >
      {loading ? (
        <Box display="flex" justifyContent="center" width="100%">
          <LinearProgress 
            sx={{ width: "100%", height: "4px" }}
            color="primary"
          />
        </Box>
      ) : (
        <>
          <WorkflowCommentsModal
            isOpen={isWorkflowCommentsOpen}
            onCloseModal={closeWorkflowComments}
            onSubmit={onWorkflowCommentsModalSubmitted}
          />

          {viewMode === "icons" ? (
            loadingActionId ? (
              <Box display="flex" justifyContent="center" width="100%">
                <Spinner small={true} />
              </Box>
            ) : (
            workflow?.transitions
              ?.filter((x) =>
                !!stateId
                  ? x.workflowStateFrom?.id === stateId
                  : x.workflowStateFrom == null
              )
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((action, index) => (
                <Tooltip 
                  key={index}
                  title={`${action.labelKey?intl.get(action.labelKey):action.actionName}`}
                >
                  <Button
                    onClick={() =>
                      handleClick(action.idWorkflowStateTo, action.allowComments)
                    }
                    sx={{
                      minWidth: "30px",
                      height: "30px",
                      padding: 0,
                      backgroundColor: action.displayColor || "#005BAF",
                      color: "#fff",
                      zIndex: 10,
                      cursor: "pointer",
                      borderRadius: "50%",
                      "&:hover": {
                        filter: `brightness(0.9)`,
                      },

                    }}
                  >
                    <MuiIconDisplay
                      iconName={action.icon}
                      sx={{
                        fontSize: 20,
                        color: "#fff"
                      }}
                    />
                  </Button>
                </Tooltip>
              ))
            )
          ) : (
            <>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleBackClick}
              >
                {intl.get("workflow.action.cancel")}
              </Button>

              {workflow?.transitions
                ?.filter((x) =>
                  !!stateId
                    ? x.workflowStateFrom?.id === stateId
                    : x.workflowStateFrom == null
                )
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((action, index) => (
                  <Button
                    key={index}
                    variant="contained"
                    onClick={() =>
                      handleClick(
                        action.idWorkflowStateTo,
                        action.allowComments
                      )
                    }
                    sx={{
                      backgroundColor: action.displayColor || "#005BAF",
                      "&:hover": {
                        backgroundColor: action.displayColor || "#004b90",
                      },
                    }}
                    disabled={disabled}
                  >
                    {action.labelKey
                      ? intl.get(action.labelKey)
                      : action.actionName}
                  </Button>
                ))}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default WorkflowActionsBar;
